import { ExternalLink, FlaskConical, Users, TrendingUp, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { fmtEuro, fmtEuroExact, cn } from '@/lib/utils';
import type { Campaign } from '@/lib/types';

function pct(actual: number, target: number) {
  return target > 0 ? Math.min(100, (actual / target) * 100) : 0;
}

export function CampaignDetail({ campaign, onClose }: { campaign: Campaign; onClose?: () => void }) {
  const c = campaign;

  const sent = c.kpi_actuals.sent ?? 0;
  const opened = c.kpi_actuals.opened ?? 0;
  const clicked = c.kpi_actuals.clicked ?? 0;
  const openRate = sent > 0 ? opened / sent : 0;
  const ctr = sent > 0 ? clicked / sent : 0;

  // Rentabilité
  const revenue = c.kpi_actuals.revenue_eur ?? 0;
  const cost = c.costs?.total_attributed_eur ?? 0;
  const net = revenue - cost;
  const roiPct = cost > 0 ? (net / cost) * 100 : null; // ROI % = (gain - coût) / coût
  const roiTargetNet = (c.kpi_targets.revenue_eur ?? 0) - cost;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">{c.name}</h2>
          <p className="text-sm text-muted-foreground">{c.objective}</p>
        </div>
        {onClose && <Button variant="ghost" size="sm" onClick={onClose}>Fermer</Button>}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiBox label="Revenue" actual={fmtEuro(c.kpi_actuals.revenue_eur ?? 0)} target={fmtEuro(c.kpi_targets.revenue_eur ?? 0)} progress={pct(c.kpi_actuals.revenue_eur ?? 0, c.kpi_targets.revenue_eur ?? 0)} highlight />
        <KpiBox label="Conversions" actual={String(c.kpi_actuals.conversions ?? 0)} target={String(c.kpi_targets.conversions ?? 0)} progress={pct(c.kpi_actuals.conversions ?? 0, c.kpi_targets.conversions ?? 0)} />
        <KpiBox label="Open rate" actual={`${Math.round(openRate * 100)}%`} target={`${Math.round((c.kpi_targets.open_rate ?? 0) * 100)}%`} progress={pct(openRate, c.kpi_targets.open_rate ?? 0)} />
        <KpiBox label="CTR" actual={`${Math.round(ctr * 100 * 10) / 10}%`} target={`${Math.round((c.kpi_targets.ctr ?? 0) * 100)}%`} progress={pct(ctr, c.kpi_targets.ctr ?? 0)} />
      </div>

      {/* Rentabilité / ROI */}
      {c.costs && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Rentabilité (ROI)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-lg border bg-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Coût imputé</div>
                <div className="num text-2xl font-bold text-warning">{fmtEuroExact(cost)}</div>
                <div className="text-[11px] text-muted-foreground">campagne</div>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Revenue</div>
                <div className="num text-2xl font-bold text-success">{fmtEuro(revenue)}</div>
                <div className="text-[11px] text-muted-foreground">encaissé</div>
              </div>
              <div className="rounded-lg border bg-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Marge nette</div>
                <div className={cn('num text-2xl font-bold', net >= 0 ? 'text-success' : 'text-destructive')}>{fmtEuro(net)}</div>
                <div className="text-[11px] text-muted-foreground">revenue − coût</div>
              </div>
              <div className="rounded-lg border border-info bg-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">ROI</div>
                <div className="num text-2xl font-bold text-info">{roiPct === null ? '—' : `${Math.round(roiPct)}%`}</div>
                <div className="text-[11px] text-muted-foreground">cible {roiTargetNet > 0 && cost > 0 ? `${Math.round((roiTargetNet / cost) * 100)}%` : '—'}</div>
              </div>
            </div>
            <Separator />
            <div className="space-y-1 text-xs">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Détail des coûts</div>
              {c.costs.items.map((it, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5"><Wallet className="h-3 w-3 text-muted-foreground" /> {it.label}</span>
                  <span className="num text-muted-foreground">
                    {fmtEuroExact(it.amount_eur)}{it.period === 'annual' ? '/an' : it.period === 'monthly' ? '/mois' : ''}
                    {' → '}<strong className="text-foreground">{fmtEuroExact(it.attributed_eur)} imputé</strong>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Audience</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Liste</span>
              <span className="font-semibold">{c.audience.list ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total contacts</span>
              <span className="num font-semibold">{c.audience.size}</span>
            </div>
            {c.audience.segments && c.audience.segments.length > 0 && (
              <>
                <Separator className="my-2" />
                <div className="text-xs uppercase text-muted-foreground tracking-wide">Segments</div>
                <ul className="space-y-1">
                  {c.audience.segments.map((s) => (
                    <li key={s.key} className="flex items-center justify-between text-sm">
                      <span>{s.label}</span>
                      <span className="num text-muted-foreground">{s.size} <span className="text-xs">({Math.round(s.size / c.audience.size * 100)}%)</span></span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">Funnel d'envoi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <FunnelStep label="Envoyés" value={sent} max={c.audience.size} />
            <FunnelStep label="Délivrés" value={c.kpi_actuals.delivered ?? 0} max={sent || 1} />
            <FunnelStep label="Ouverts" value={opened} max={sent || 1} />
            <FunnelStep label="Cliqués" value={clicked} max={opened || 1} />
            <FunnelStep label="Bookings Cal.com" value={c.kpi_actuals.bookings_calcom ?? 0} max={c.kpi_targets.bookings_calcom ?? 1} highlight />
            <FunnelStep label="Conversions €" value={c.kpi_actuals.conversions ?? 0} max={c.kpi_targets.conversions ?? 1} highlight />
          </CardContent>
        </Card>
      </div>

      {c.ab_test?.enabled && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><FlaskConical className="h-4 w-4" /> A/B Test ({c.ab_test.variable})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <div className={cn('rounded-md border p-2', c.ab_test.winner === 'a' && 'border-success bg-success/5')}>
                <div className="text-xs uppercase text-muted-foreground">Variante A {c.ab_test.winner === 'a' && '🏆'}</div>
                <div className="font-mono text-xs mt-1">{c.ab_test.variant_a ?? '—'}</div>
              </div>
              <div className={cn('rounded-md border p-2', c.ab_test.winner === 'b' && 'border-success bg-success/5')}>
                <div className="text-xs uppercase text-muted-foreground">Variante B {c.ab_test.winner === 'b' && '🏆'}</div>
                <div className="font-mono text-xs mt-1">{c.ab_test.variant_b ?? '—'}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Split {c.ab_test.split} · Décision sur {c.ab_test.decision_metric} après {c.ab_test.decision_after_hours}h
              {c.ab_test.winner ? <> · <Badge variant="success" className="ml-1">Gagnante : {c.ab_test.winner.toUpperCase()}</Badge></> : ' · En cours'}
            </div>
          </CardContent>
        </Card>
      )}

      {c.links && c.links.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Liens utiles</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {c.links.map((l, i) => (
              <Button key={i} asChild variant="outline" size="sm">
                <a href={l.url} target="_blank" rel="noreferrer noopener">
                  {l.label} <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {c.notes && (
        <Card>
          <CardContent className="pt-4 text-sm text-muted-foreground whitespace-pre-line">{c.notes}</CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiBox({ label, actual, target, progress, highlight }: { label: string; actual: string; target: string; progress: number; highlight?: boolean }) {
  return (
    <div className={cn('rounded-lg border bg-card p-3', highlight && 'border-info')}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="num text-2xl font-bold leading-tight mt-1">{actual}</div>
      <div className="num text-xs text-muted-foreground">/ {target}</div>
      <Progress className="mt-2 h-1" value={progress} indicatorClassName={progress >= 100 ? 'bg-success' : 'bg-info'} />
    </div>
  );
}

function FunnelStep({ label, value, max, highlight }: { label: string; value: number; max: number; highlight?: boolean }) {
  const p = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between">
        <span className={cn(highlight && 'font-semibold')}>{label}</span>
        <span className="num text-xs text-muted-foreground">{value} {max > 0 && <>· {Math.round(p)}%</>}</span>
      </div>
      <Progress value={p} className="h-1.5" indicatorClassName={highlight ? 'bg-success' : 'bg-primary'} />
    </div>
  );
}
